import React, { useEffect, useMemo, useState } from 'react';
import { Edit3, Plus, Save, Search, Trash2, Users as UsersIcon, X } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { userService } from '../services/api';
import { useAuthStore } from '../store';
import { toast } from 'react-toastify';

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  cpf: '',
  role: 'user',
  password: '',
  is_active: true,
};

const roleLabels = {
  admin: 'Administrador',
  operator: 'Operador',
  user: 'Usuário',
};

const Users = () => {
  const currentUser = useAuthStore((state) => state.user);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;

    return users.filter((user) => (
      user.name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.phone?.toLowerCase().includes(term) ||
      roleLabels[user.role]?.toLowerCase().includes(term)
    ));
  }, [search, users]);

  const loadUsers = async () => {
    setLoading(true);

    try {
      const response = await userService.getAll();
      setUsers(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      cpf: user.cpf || '',
      role: user.role || 'user',
      password: '',
      is_active: user.is_active !== false,
    });
    setModalOpen(true);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!editingUser && formData.password.length < 8) {
      toast.error('A senha inicial precisa ter pelo menos 8 caracteres');
      return;
    }

    if (editingUser && formData.password && formData.password.length < 8) {
      toast.error('A nova senha precisa ter pelo menos 8 caracteres');
      return;
    }

    setSaving(true);

    try {
      const payload = { ...formData };

      if (editingUser && !payload.password) {
        delete payload.password;
      }

      if (editingUser) {
        const response = await userService.update(editingUser.id, payload);
        setUsers((current) => current.map((user) => (
          user.id === editingUser.id ? response.data.data : user
        )));
        toast.success('Usuário atualizado com sucesso');
      } else {
        const response = await userService.create(payload);
        setUsers((current) => [response.data.data, ...current]);
        toast.success('Usuário criado com sucesso');
      }

      setModalOpen(false);
      setEditingUser(null);
      setFormData(emptyForm);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar usuário');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (user) => {
    const confirmed = window.confirm(`Desativar ${user.name}?`);
    if (!confirmed) return;

    try {
      const response = await userService.delete(user.id);
      setUsers((current) => current.map((item) => (
        item.id === user.id ? response.data.data : item
      )));
      toast.success('Usuário desativado');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao desativar usuário');
    }
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Usuários</h1>
              <p className="text-dark-400">Gerencie administradores, operadores e usuários do sistema.</p>
            </div>

            <button
              type="button"
              onClick={openCreateModal}
              className="btn-primary flex items-center justify-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Novo usuário
            </button>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="card">
              <p className="text-sm text-dark-400">Total</p>
              <p className="mt-2 text-3xl font-bold text-white">{users.length}</p>
            </div>
            <div className="card">
              <p className="text-sm text-dark-400">Ativos</p>
              <p className="mt-2 text-3xl font-bold text-success-400">
                {users.filter((user) => user.is_active !== false).length}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-dark-400">Administradores</p>
              <p className="mt-2 text-3xl font-bold text-primary-400">
                {users.filter((user) => user.role === 'admin').length}
              </p>
            </div>
          </div>

          <div className="card mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-dark-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="input-field pl-11"
                placeholder="Buscar por nome, email, telefone ou perfil"
              />
            </div>
          </div>

          <div className="card p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left">
                <thead className="border-b border-dark-700 text-sm text-dark-400">
                  <tr>
                    <th className="px-5 py-4 font-medium">Usuário</th>
                    <th className="px-5 py-4 font-medium">Contato</th>
                    <th className="px-5 py-4 font-medium">Perfil</th>
                    <th className="px-5 py-4 font-medium">Status</th>
                    <th className="px-5 py-4 text-right font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="px-5 py-10 text-center text-dark-400">
                        Carregando usuários...
                      </td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-5 py-10 text-center text-dark-400">
                        <UsersIcon className="mx-auto mb-2 h-10 w-10 opacity-50" />
                        Nenhum usuário encontrado
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b border-dark-800 text-sm last:border-0">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-white">{user.name}</div>
                          <div className="text-dark-400">{user.email}</div>
                        </td>
                        <td className="px-5 py-4 text-dark-300">
                          <div>{user.phone || 'Sem telefone'}</div>
                          <div className="text-xs text-dark-500">{user.cpf || 'CPF não informado'}</div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`badge ${user.role === 'admin' ? 'badge-info' : user.role === 'operator' ? 'badge-warning' : 'badge-success'}`}>
                            {roleLabels[user.role] || user.role}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`badge ${user.is_active === false ? 'badge-danger' : 'badge-success'}`}>
                            {user.is_active === false ? 'Inativo' : 'Ativo'}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(user)}
                              className="rounded-lg p-2 text-primary-400 transition-colors hover:bg-dark-700"
                              title="Editar"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeactivate(user)}
                              disabled={user.id === currentUser?.id || user.is_active === false}
                              className="rounded-lg p-2 text-danger-400 transition-colors hover:bg-dark-700 disabled:cursor-not-allowed disabled:opacity-40"
                              title="Desativar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-3xl rounded-lg border border-dark-700 bg-dark-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-dark-800 p-5">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {editingUser ? 'Editar usuário' : 'Novo usuário'}
                </h2>
                <p className="text-sm text-dark-400">Defina dados, perfil e status de acesso.</p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-lg p-2 text-dark-400 transition-colors hover:bg-dark-800 hover:text-white"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Nome</label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Telefone</label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">CPF</label>
                  <input
                    name="cpf"
                    value={formData.cpf}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="000.000.000-00"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Perfil</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="input-field"
                  >
                    <option value="user">Usuário</option>
                    <option value="operator">Operador</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">
                    {editingUser ? 'Nova senha opcional' : 'Senha inicial'}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-field"
                    minLength={8}
                    required={!editingUser}
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-dark-300">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 accent-primary-500"
                  disabled={editingUser?.id === currentUser?.id}
                />
                Usuário ativo
              </label>

              <div className="flex flex-col-reverse gap-3 border-t border-dark-800 pt-5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Salvando...' : 'Salvar usuário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
